<template>
  <div>
    <v-dialog
      max-width="720"
      v-model="isDialogOpen"
    >
      <v-card
        class="pa-4"
        v-if="formattedDate"
      >
        <page-title :value="$t('foodMenu.createNewMenu', { date: formattedDate })" />

        <div class="my-4">
          <div class="category-header">
            <div class="font-weight-bold my-2">{{ $t('foodMenu.selectCategory') }}</div>
            <v-spacer />
            <v-btn
              color="primary"
              variant="text"
              size="small"
              @click="openCreateCategoryDialog"
            >+ {{ $t('foodMenu.addCategory') }}</v-btn>
          </div>
          <div>
            <div class="category-table-headers">
              <div class="category-table-header col-checkbox"></div>
              <div class="category-table-header col-name">{{ $t('foodMenu.categoryName') }}</div>
              <div class="category-table-header col-classes">{{ $t('foodMenu.targetClasses') }}</div>
            </div>
            <div 
              class="category-table-row"
              v-for="(c, i) in menuCategories"
              :key="i"
              @click="categoryId = c.categoryId"
            >
              <div class="category-table-item col-checkbox">
                <v-icon v-if="categoryId === c.categoryId" color="primary">mdi-radiobox-marked</v-icon>
                <v-icon v-else>mdi-radiobox-blank</v-icon>
              </div>
              <div class="category-table-item col-name"> {{ c.name }} </div>
              <div class="category-table-item col-classes"> {{ formatClassNames(c.targetClasses) }} </div>
            </div>
          </div>
          <v-divider />
        </div>

        <div class="my-4">
          <v-textarea
            v-model="body"
            variant="outlined"
            :label="$t('foodMenu.menu')"
            hide-details
            color="primary"
          />
        </div>
        <div class="my-4">
          <v-textarea
            v-model="note"
            variant="outlined"
            :label="$t('foodMenu.note')"
            hide-details
            color="primary"
          />
        </div>
        <div class="my-4">
          <v-select
            v-model="allergenIds"
            variant="outlined"
            hide-details
            :label="$t('foodMenu.allergenFoods')"
            :items="allergenOptions"
            item-title="displayName"
            item-value="allergenId"
            multiple
            color="primary"
          />
        </div>

        <v-progress-linear v-if="isCreating" indeterminate color="primary" class="my-4" />

        <div class="my-4 d-flex align-center">
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!creationValid || isCreating"
            @click="createMenu"
          >{{ $t('common.create') }}</v-btn>
          <v-spacer />
        </div>
      </v-card>
    </v-dialog>

    <food-category-create-container />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { foodMenusModule } from '@/store/viewModules/food-menu/foodMenusModule';
import dayjs from 'dayjs';
import 'dayjs/locale/en';

import PageTitle from '@/components/atoms/PageTitle.vue';
import FoodCategoryCreateContainer from '@/containers/food-menu/FoodCategoryCreateContainer.vue';
import { NurseryClassResponse } from 'chaild-api/lib';

interface DataType {
  isCreating: boolean;

  categoryId: number | null;
  body: string;
  note: string;
  allergenIds: number[];
}

export default defineComponent({
  components: {
    PageTitle,
    FoodCategoryCreateContainer,
  },

  data: (): DataType => ({
    isCreating: false,

    categoryId: null,
    body: '',
    note: '',
    allergenIds: [],
  }),

  computed: {
    menuCategories() {
      return foodMenusModule.menuCategories;
    },
    originalAllergens() {
      return foodMenusModule.allergens;
    },
    allergenOptions(): { allergenId: number; name: string; modifiable: boolean; displayName: string }[] {
      const allergenNameKeys: Record<string, string> = {
        '小麦': 'foodMenu.allergenNames.wheat',
        '大豆': 'foodMenu.allergenNames.soybean',
        '牛肉': 'foodMenu.allergenNames.beef',
        '卵': 'foodMenu.allergenNames.egg',
        '乳': 'foodMenu.allergenNames.milk',
        '落花生': 'foodMenu.allergenNames.peanut',
        '蕎麦': 'foodMenu.allergenNames.buckwheat',
        'エビ': 'foodMenu.allergenNames.shrimp',
        'カニ': 'foodMenu.allergenNames.crab',
        'サバ': 'foodMenu.allergenNames.mackerel',
        '豚肉': 'foodMenu.allergenNames.pork',
        '鶏肉': 'foodMenu.allergenNames.chicken',
        'ゼラチン': 'foodMenu.allergenNames.gelatin',
        'オレンジ': 'foodMenu.allergenNames.orange',
        'りんご': 'foodMenu.allergenNames.apple',
        '桃': 'foodMenu.allergenNames.peach',
        '山芋': 'foodMenu.allergenNames.yam',
        'いか': 'foodMenu.allergenNames.squid',
        'ごま': 'foodMenu.allergenNames.sesame',
        'くるみ': 'foodMenu.allergenNames.walnut',
        'あわび': 'foodMenu.allergenNames.abalone',
        'いくら': 'foodMenu.allergenNames.salmonRoe',
        'ｶｼｭｰﾅｯﾂ': 'foodMenu.allergenNames.cashewNut',
        'ｷｳｲﾌﾙｰﾂ': 'foodMenu.allergenNames.kiwiFruit',
        '鮭': 'foodMenu.allergenNames.salmon',
        'バナナ': 'foodMenu.allergenNames.banana',
        'まつたけ': 'foodMenu.allergenNames.matsutake',
        'タコ': 'foodMenu.allergenNames.octopus',
        'ホタテ': 'foodMenu.allergenNames.scallop',
        'あさり': 'foodMenu.allergenNames.clam',
      };
      return this.originalAllergens.map((allergen) => ({
        ...allergen,
        displayName: allergenNameKeys[allergen.name]
          ? (this.$t(allergenNameKeys[allergen.name]) as string)
          : allergen.name,
      }));
    },
    creatingDate() {
      return foodMenusModule.creatingDate;
    },
    formattedDate(): string | null {
      if (this.creatingDate) {
        return dayjs(this.creatingDate).locale('en').format('DD/MM (ddd)');
      }
      return null;
    },
    isDialogOpen: {
      get(): boolean {
        return !!this.creatingDate;
      },
      set(val: boolean) {
        if (!val) {
          this.closeDialog();
        }
      }
    },
    creationValid(): boolean {
      if (
        this.categoryId
        && this.body.trim().length > 0
      ) {
        return true;
      }
      return false;
    }
  },

  methods: {
    async createMenu() {
      if (this.categoryId) {
        this.isCreating = true;
        await foodMenusModule.createMenu({
          categoryId: this.categoryId,
          body: this.body,
          note: this.note,
          allergenIds: this.allergenIds,
        });
        this.isCreating = false;
        this.closeDialog();
      }
    },
    clear() {
      this.categoryId = null;
      this.body = '';
      this.note = '';
      this.allergenIds = [];
    },
    closeDialog() {
      this.clear();
      foodMenusModule.setCreatingDate(null);
    },
    openCreateCategoryDialog() {
      foodMenusModule.setIsCreateCategoryDialogOpen(true);
    },
    formatClassNames(classes: NurseryClassResponse[]) {
      if (!classes || classes.length === 0) return '';
      return classes.map((c) => c.className).filter(Boolean).join(', ');
    }
  }
});
</script>

<style lang="scss" scoped>
.category-header {
  padding: 0 8px;
  font-size: 14px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
}

.category-table-headers {
  display: flex;
  align-items: center;
  padding: 4px;
  height: 30px;
  border-bottom: solid 1px #e0e0e0;
}

.category-table-header {
  font-size: 12px;
  font-weight: bold;
}

.category-table-row {
  display: flex;
  align-items: center;
  height: 40px;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }
}

.category-table-item {
  font-size: 14px;
}

.col-checkbox {
  width: 40px;
  padding-left: 8px;
}

.col-name {
  width: 160px;
}

.col-classes {
  flex: 1;
}
</style>